import React from "react";
import { NewsItem } from "../common/types";
import { formatDate } from "../utils";

/**
 * One news item as a row.
 *
 * This replaces a card whose top 40% was a generated abstract image from
 * /images/news/icon_N.webp, chosen by list position — only 14 of 82 items have
 * a real image_url, so for 68 of them the picture was decoration standing in
 * for nothing, and it pushed the title into a translucent box over a busy
 * gradient. The links the data already carries (paper_pdf_url on 28 items,
 * github_url on 32, authors on 68) were not shown at all; they are now.
 */

/** Descriptions are markdown. The old card sliced the raw string, so every
 *  summary ended mid-word on things like "multimod...". Strip, then cut on a
 *  word boundary. */
export const plainSummary = (md: string, max = 190): string => {
  const text = (md || "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`#>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  return cut.slice(0, cut.lastIndexOf(" ")) + "…";
};

interface Props {
  news: NewsItem;
  onClick: (article: NewsItem) => void;
}

const stop = (e: React.MouseEvent) => e.stopPropagation();

const NewsRow: React.FC<Props> = ({ news, onClick }) => (
  <li
    className="grid grid-cols-[92px_minmax(0,1fr)] gap-5 py-4 border-b border-secondary cursor-pointer group"
    onClick={() => onClick(news)}
  >
    <div className="font-mono text-xs text-secondary pt-1">
      {formatDate(news.launch_date)}
    </div>
    <div className="min-w-0">
      <h3 className="text-base font-semibold text-primary leading-snug group-hover:text-accent transition-colors">
        {news.title}
      </h3>
      <p className="text-sm text-secondary mt-1 leading-relaxed">
        {plainSummary(news.description)}
      </p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-secondary">
        {news.authors && news.authors.length > 0 && (
          <span className="truncate max-w-[22rem]">{news.authors.join(", ")}</span>
        )}
        {news.paper_pdf_url && (
          <a className="text-accent hover:underline" href={news.paper_pdf_url} onClick={stop} target="_blank" rel="noopener noreferrer">
            paper
          </a>
        )}
        {news.github_url && (
          <a className="text-accent hover:underline" href={news.github_url} onClick={stop} target="_blank" rel="noopener noreferrer">
            code
          </a>
        )}
        {news.launch_url && (
          <a className="text-accent hover:underline" href={news.launch_url} onClick={stop} target="_blank" rel="noopener noreferrer">
            announcement
          </a>
        )}
        {(news.tags || []).slice(0, 3).map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-wide border border-secondary rounded px-1.5 py-0.5">
            {t}
          </span>
        ))}
      </div>
    </div>
  </li>
);

export default NewsRow;
